import { useEffect, useRef } from 'react';
import type Konva from 'konva';

import { CanvasTestingBridge } from '../../../testing/CanvasTestingBridge';
import { useDiagramStore } from '../../../stores/diagramStore';
import { useFieldVisibilityStore } from '../../../stores/fieldVisibilityStore';
import { getNodeMutationManager } from '../../../core/commands/mutations/NodeMutationManager';
import { MoveNodesCommand } from '../../../core/commands/TransformCommands';
import { Clipboard } from '../../../core/clipboard/Clipboard';
import { buildClipboardSelection, hasClipboardContent } from '../../../core/clipboard/clipboardSelection';
import { ChangeNodeStyleCommand } from '../../../core/commands/ChangeNodeStyleCommand';
import { DockNodeToTableCellCommand } from '../../../core/commands/DockNodeToTableCellCommand';
import { ModifyTableGridAtIndexCommand } from '../../../core/commands/ModifyTableGridAtIndexCommand';
import { ReorderTableAxisCommand } from '../../../core/commands/ReorderTableAxisCommand';
import { ResizeTableGridCommand } from '../../../core/commands/ResizeTableGridCommand';
import { ScaleTableCommand } from '../../../core/commands/ScaleTableCommand';
import { UpdateTableCellTextCommand } from '../../../core/commands/UpdateTableCellTextCommand';
import { UpdateTableGridCommand } from '../../../core/commands/UpdateTableGridCommand';
import { UpdateTableCellStylesCommand } from '../../../core/commands/UpdateTableCellStylesCommand';
import { ClearTableCellStylesCommand } from '../../../core/commands/ClearTableCellStylesCommand';
import { UpdateTableCellLayoutsCommand } from '../../../core/commands/UpdateTableCellLayoutsCommand';
import { ToggleTableMergeCommand } from '../../../core/commands/ToggleTableMergeCommand';
import { AutoResizeTableCellToFitDockedContentCommand } from '../../../core/commands/AutoResizeTableCellToFitDockedContentCommand';
import { PasteTableCellContentCommand } from '../../../core/commands/PasteTableCellContentCommand';
import { createTableFromSchema } from '../../../domains/diagram/shapes/table/createTableFromSchema';
import { getTableCellRectByKey, getTableDropHitAtPoint } from '../../../domains/diagram/shapes/table/tableDrop.utils';
import { TableCellClipboard } from '../../../domains/diagram/shapes/table/tableCellClipboard';
import i18n from '../../../i18n/config';
import { useTableCellStore, type TableCellKey } from '../../../stores/tableCellStore';

export function useCanvasTestHarness(input: {
  stageRef: { current: Konva.Stage | null };
  selectedIds: Set<string>;
  testBridgeRef: { current: CanvasTestingBridge | null };
  testCanWriteOverrideRef: { current: boolean | null };

  toolRegistry: any;
  commandManager: any;
  setSelected: (next: Set<string>) => void;
  setShapePickerState: (updater: any) => void;

  createDeleteNodeCommandWithComments: (nodeIds: string[]) => any;
  commitViewportStage?: (next: { scale: number; offsetX: number; offsetY: number }) => void;
}): void {
  const {
    stageRef,
    selectedIds,
    testBridgeRef,
    testCanWriteOverrideRef,
    toolRegistry,
    commandManager,
    setSelected,
    setShapePickerState,
    createDeleteNodeCommandWithComments,
    commitViewportStage,
  } = input;

  const commitViewportStageRef = useRef(commitViewportStage);
  commitViewportStageRef.current = commitViewportStage;

  // Initialize testing bridge (retry until stage is ready in test mode)
  useEffect(() => {
    const viteTest = (import.meta as any)?.env?.VITE_TEST;
    const isDev = (import.meta as any)?.env?.MODE !== 'production';
    const isLocalhost =
      typeof window !== 'undefined' && ['localhost', '127.0.0.1', '::1', '[::1]'].includes(window.location.hostname);
    const urlTest = typeof window !== 'undefined' && window.location.search.includes('test=true');
    const pathnameOk =
      typeof window !== 'undefined' &&
      (window.location.pathname.startsWith('/test-canvas') || window.location.pathname.startsWith('/f/'));
    const isTest = pathnameOk && isDev && isLocalhost && ((viteTest === '1' || viteTest === 'true') || urlTest);
    if (!isTest) return;
    let cancelled = false;
    const tryInit = () => {
      if (cancelled) return;
      if (stageRef.current && !testBridgeRef.current) {
        testBridgeRef.current = new CanvasTestingBridge(stageRef.current, selectedIds);
        testBridgeRef.current.setViewportCommitter((next) => commitViewportStageRef.current?.(next));
        try {
          (window as any).__testCreatePerson = (sex: 'male' | 'female' | 'unknown', x: number, y: number) => {
            const id = `shape-${Date.now()}-${Math.random().toString(36).slice(2)}`;
            const node: any = { id, type: 'person', x, y, w: 60, h: 60, sex, name: 'Test' };
            try {
              (useDiagramStore as any).getState().addNode(node);
            } catch { }
            return id;
          };
        } catch { }
        return;
      }
      requestAnimationFrame(tryInit);
    };
    tryInit();
    return () => {
      cancelled = true;
    };
    // Preserve existing behavior: this effect intentionally runs once in test mode.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Expose deterministic TestHarness API and readiness for E2E tests
  useEffect(() => {
    const viteTest = (import.meta as any)?.env?.VITE_TEST;
    const isDev = (import.meta as any)?.env?.MODE !== 'production';
    const isLocalhost =
      typeof window !== 'undefined' && ['localhost', '127.0.0.1', '::1', '[::1]'].includes(window.location.hostname);
    const urlTest = typeof window !== 'undefined' && window.location.search.includes('test=true');
    const pathnameOk =
      typeof window !== 'undefined' &&
      (window.location.pathname.startsWith('/test-canvas') || window.location.pathname.startsWith('/f/'));
    const isTest = pathnameOk && isDev && isLocalhost && ((viteTest === '1' || viteTest === 'true') || urlTest);
    if (!isTest) return;

    const testState = ((window as any).__canvasTestHarnessState ??= {
      pasteHistory: [] as Array<{ nodes: any[]; edges: any[] }>,
      pasteRedoHistory: [] as Array<{ nodes: any[]; edges: any[] }>,
      cutClipboardData: null as { nodes: any[]; edges: any[] } | null,
      hiddenPastedNodeIds: new Set<string>(),
      hiddenCutNodeIds: new Set<string>(),
      editingTableCells: new Set<string>(),
    });
    let cancelled = false;
    let onTableNavigationKeyDown: ((event: KeyboardEvent) => void) | null = null;
    const setup = () => {
      if (cancelled) return;
      const stage = stageRef.current;
      if (!stage) {
        requestAnimationFrame(setup);
        return;
      }

      // The E2E harness needs an immediately observable node, so test-mode
      // creation writes through the store's test bypass before exposing its id.
      const createNodeUndoable = (node: any): string => {
        try {
          (useDiagramStore as any).getState().addNode(node, true);
          const store = (useDiagramStore as any).getState();
          const point = { x: node.x ?? 0, y: node.y ?? 0 };
          const hit = (store.nodes ?? [])
            .filter((candidate: any) => candidate?.type === 'table')
            .map((table: any) => getTableDropHitAtPoint(table, point))
            .find(Boolean);
          if (hit) {
            void commandManager.execute(new DockNodeToTableCellCommand(
              node.id,
              { x: hit.contentRect.x, y: hit.contentRect.y },
              { x: node.x ?? 0, y: node.y ?? 0 },
              { mode: 'dock', dock: { tableId: hit.tableId, cellKey: hit.cellKey, offset: { x: 0, y: 0 } } },
            ));
          }
        } catch { }
        return node.id;
      };

      const harness = {
        createShape: (params: { type: 'male' | 'female' | 'unknown'; x: number; y: number }) => {
          const id = `shape-${Date.now()}-${Math.random().toString(36).slice(2)}`;
          const sex = params?.type === 'male' ? 'male' : params?.type === 'female' ? 'female' : 'unknown';
          return createNodeUndoable({
            id,
            type: 'person',
            x: params?.x ?? 0,
            y: params?.y ?? 0,
            w: 60,
            h: 60,
            sex,
            name: 'Test',
          });
        },
        createBasicShape: (params: { variant?: string; x: number; y: number; w?: number; h?: number }) => {
          const id = `shape-${Date.now()}-${Math.random().toString(36).slice(2)}`;
          const variant = typeof params?.variant === 'string' ? params.variant : 'rectangle';
          return createNodeUndoable({
            id,
            type: 'basic-shape',
            x: params?.x ?? 0,
            y: params?.y ?? 0,
            w: params?.w ?? 120,
            h: params?.h ?? 80,
            width: params?.w ?? 120,
            height: params?.h ?? 80,
            name: '',
            text: '',
            attrs: { variant, category: 'basic', label: '', text: '' },
          });
        },
        createStickyNote: (params: { x: number; y: number; w?: number; h?: number; text?: string; fontSize?: number }) => {
          const id = `shape-${Date.now()}-${Math.random().toString(36).slice(2)}`;
          return createNodeUndoable({
            id,
            type: 'sticky-note',
            x: params?.x ?? 0,
            y: params?.y ?? 0,
            w: params?.w ?? 140,
            h: params?.h ?? 120,
            text:
              params?.text ??
              (i18n.t('genogram:shapes.stickyNoteDefault', { defaultValue: 'Note' }) as string),
            fontSize: params?.fontSize ?? 16,
          });
        },
        createFreehand: (params: {
          points: number[];
          color?: string;
          width?: number;
          opacity?: number;
          mode?: 'pen' | 'marker' | 'highlighter' | 'eraser';
        }) => {
          const id = `path-${Date.now()}-${Math.random().toString(36).slice(2)}`;
          const points = Array.isArray(params?.points) ? params.points : [];
          return createNodeUndoable({
            id,
            type: 'freehand',
            x: 0,
            y: 0,
            width: 0,
            height: 0,
            attrs: {
              points: points.slice(),
              strokeColor: params?.color ?? '#111827',
              strokeWidth: params?.width ?? 2,
              opacity: params?.opacity ?? 1,
              lineCap: 'round',
              lineJoin: 'round',
              tension: 0.5,
              mode: params?.mode ?? 'pen',
            },
          });
        },
        createText: (params: { x: number; y: number; w?: number; h?: number; text?: string; fontSize?: number }) => {
          const id = `shape-${Date.now()}-${Math.random().toString(36).slice(2)}`;
          return createNodeUndoable({
            id,
            type: 'text',
            x: params?.x ?? 0,
            y: params?.y ?? 0,
            w: params?.w ?? 180,
            h: params?.h ?? 60,
            text: params?.text ?? 'Text',
            fontSize: params?.fontSize ?? 16,
          });
        },
        createTable: async (params: { x: number; y: number; schemaId?: string }) => {
          try {
            const command = createTableFromSchema(params?.schemaId ?? 'blank', {
              x: params?.x ?? 0,
              y: params?.y ?? 0,
            });
            await commandManager.execute(command);
            return command.getCreatedId() ?? null;
          } catch (error) {
            console.error('TestHarness.createTable error:', error);
            return null;
          }
        },
        selectTableCell: (tableId: string, params: { row: number; col: number; extend?: boolean }) => {
          const key = `${params.row},${params.col}` as TableCellKey;
          const cellStore = useTableCellStore.getState();
          const previous = cellStore.selections[tableId];
          const selectedKeys = params.extend
            ? Array.from(new Set([...(previous?.selectedKeys ?? []), key])) as TableCellKey[]
            : [key];
          cellStore.setSelection(tableId, { anchorKey: previous?.anchorKey ?? key, selectedKeys });
          (useDiagramStore as any).getState().setSelected(new Set([tableId]));
          setSelected(new Set([tableId]));
          toolRegistry?.updateContext({ selection: new Set([tableId]) });
        },
        selectTableCellRange: (tableId: string, params: { startRow: number; startCol: number; endRow: number; endCol: number }) => {
          const selectedKeys: TableCellKey[] = [];
          for (let row = Math.min(params.startRow, params.endRow); row <= Math.max(params.startRow, params.endRow); row += 1) {
            for (let col = Math.min(params.startCol, params.endCol); col <= Math.max(params.startCol, params.endCol); col += 1) {
              selectedKeys.push(`${row},${col}` as TableCellKey);
            }
          }
          useTableCellStore.getState().setSelection(tableId, {
            anchorKey: `${params.startRow},${params.startCol}` as TableCellKey,
            selectedKeys,
          });
          (useDiagramStore as any).getState().setSelected(new Set([tableId]));
          setSelected(new Set([tableId]));
          toolRegistry?.updateContext({ selection: new Set([tableId]) });
          return true;
        },
        getTableCellSelection: (tableId: string) => {
          return useTableCellStore.getState().selections[tableId] ?? { selectedKeys: [] };
        },
        setTableCellText: async (tableId: string, params: { row: number; col: number; text: string }) => {
          await commandManager.execute(new UpdateTableCellTextCommand({
            nodeId: tableId,
            cellKey: `${params.row},${params.col}` as TableCellKey,
            newText: params.text,
          }));
        },
        reorderTableRow: async (tableId: string, params: { fromIndex: number; toIndex: number }) => {
          await commandManager.execute(new ReorderTableAxisCommand(tableId, 'row', params.fromIndex, params.toIndex));
        },
        reorderTableColumn: async (tableId: string, params: { fromIndex: number; toIndex: number }) => {
          await commandManager.execute(new ReorderTableAxisCommand(tableId, 'col', params.fromIndex, params.toIndex));
        },
        resizeTableRow: async (tableId: string, params: { index: number; delta: number }) => {
          const table = (useDiagramStore as any).getState().nodes?.find?.((node: any) => node.id === tableId);
          if (!table) return false;
          const attrs = table.attrs ?? {};
          const rowHeights = [...(attrs.rowHeights ?? Array(attrs.rowCount ?? 3).fill(20))];
          const headerHeight = attrs.headerHeight ?? 32;
          const renderIndex = params.index;
          if (attrs.showHeader !== false && renderIndex === 0) {
            await commandManager.execute(new ResizeTableGridCommand({
              tableId,
              rowHeights,
              columnWidths: [...(attrs.columnWidths ?? Array(attrs.columnCount ?? 3).fill((table.width ?? table.w ?? 240) / (attrs.columnCount ?? 3)))],
              headerHeight: headerHeight + params.delta,
            }));
            return true;
          }
          const bodyIndex = attrs.showHeader !== false ? renderIndex - 1 : renderIndex;
          if (bodyIndex < 0 || bodyIndex >= rowHeights.length) return false;
          rowHeights[bodyIndex] += params.delta;
          await commandManager.execute(new ResizeTableGridCommand({
            tableId,
            rowHeights,
            columnWidths: [...(attrs.columnWidths ?? Array(attrs.columnCount ?? 3).fill((table.width ?? table.w ?? 240) / (attrs.columnCount ?? 3)))],
            headerHeight,
          }));
          return true;
        },
        resizeTableColumn: async (tableId: string, params: { index: number; delta: number }) => {
          const table = (useDiagramStore as any).getState().nodes?.find?.((node: any) => node.id === tableId);
          if (!table) return false;
          const attrs = table.attrs ?? {};
          const columnWidths = [...(attrs.columnWidths ?? Array(attrs.columnCount ?? 3).fill((table.width ?? table.w ?? 240) / (attrs.columnCount ?? 3)))];
          if (params.index < 0 || params.index >= columnWidths.length) return false;
          columnWidths[params.index] += params.delta;
          await commandManager.execute(new ResizeTableGridCommand({
            tableId,
            rowHeights: [...(attrs.rowHeights ?? Array(attrs.rowCount ?? 3).fill(20))],
            columnWidths,
            headerHeight: attrs.headerHeight ?? 32,
          }));
          return true;
        },
        scaleTableByDelta: async (tableId: string, params: { deltaWidth: number; deltaHeight: number }) => {
          const table = (useDiagramStore as any).getState().nodes?.find?.((node: any) => node.id === tableId);
          if (!table) return false;
          const width = table.width ?? table.w ?? 240;
          const height = table.height ?? table.h ?? 180;
          await commandManager.execute(new ScaleTableCommand({
            nodeId: tableId,
            scaleX: (width + params.deltaWidth) / width,
            scaleY: (height + params.deltaHeight) / height,
            position: { x: table.x ?? 0, y: table.y ?? 0 },
          }));
          return true;
        },
        insertTableRow: async (tableId: string, params: { index: number; direction: 'before' | 'after' }) => {
          await commandManager.execute(new ModifyTableGridAtIndexCommand([tableId], {
            axis: 'row',
            op: 'insert',
            position: params.direction,
            rowIndexByNodeId: { [tableId]: params.index },
          }));
          return true;
        },
        insertTableColumn: async (tableId: string, params: { index: number; direction: 'before' | 'after' }) => {
          await commandManager.execute(new ModifyTableGridAtIndexCommand([tableId], {
            axis: 'column',
            op: 'insert',
            position: params.direction,
            colIndexByNodeId: { [tableId]: params.index },
          }));
          return true;
        },
        dockShapeToCell: async (nodeId: string, tableId: string, params: { row: number; col: number; offset?: { x?: number; y?: number } }) => {
          const store = (useDiagramStore as any).getState();
          const table = store.nodes?.find?.((node: any) => node.id === tableId);
          const node = store.nodes?.find?.((candidate: any) => candidate.id === nodeId);
          if (!table || !node) return false;
          const cellKey = `${params.row},${params.col}` as TableCellKey;
          const hit = getTableCellRectByKey(table, cellKey);
          if (!hit) return false;
          const offset = { x: params.offset?.x ?? 0, y: params.offset?.y ?? 0 };
          await commandManager.execute(new DockNodeToTableCellCommand(
            nodeId,
            { x: hit.contentRect.x + offset.x, y: hit.contentRect.y + offset.y },
            { x: node.x ?? 0, y: node.y ?? 0 },
            { mode: 'dock', dock: { tableId, cellKey: hit.cellKey, offset } },
          ));
          if ((table.attrs as any)?.autoResizeCells === true) {
            await commandManager.execute(new AutoResizeTableCellToFitDockedContentCommand(tableId, hit.cellKey));
          }
          return true;
        },
        getTableCellCenter: (tableId: string, params: { row: number; col: number }) => {
          const table = (useDiagramStore as any).getState().nodes?.find?.((node: any) => node.id === tableId);
          const hit = table ? getTableCellRectByKey(table, `${params.row},${params.col}` as TableCellKey) : null;
          if (!hit) return null;
          return {
            x: hit.contentRect.x + hit.contentRect.w / 2,
            y: hit.contentRect.y + hit.contentRect.h / 2,
          };
        },
        getDockedNodesInCell: (tableId: string, params: { row: number; col: number }) => {
          const cellKey = `${params.row},${params.col}`;
          return ((useDiagramStore as any).getState().nodes ?? []).filter(
            (node: any) => node?.attrs?.tableDock?.tableId === tableId && node?.attrs?.tableDock?.cellKey === cellKey,
          );
        },
        getDockedNodes: (tableId: string) => {
          return ((useDiagramStore as any).getState().nodes ?? []).filter(
            (node: any) => node?.attrs?.tableDock?.tableId === tableId,
          );
        },
        updateTableGrid: async (tableId: string, patch: Record<string, unknown>) => {
          for (const [field, value] of Object.entries(patch)) {
            await commandManager.execute(new UpdateTableGridCommand([tableId], { field: field as any, value: value as any }));
          }
          return true;
        },
        removeTableRow: async (tableId: string, params: { index: number }) => {
          await commandManager.execute(new ModifyTableGridAtIndexCommand([tableId], {
            axis: 'row', op: 'remove', rowIndexByNodeId: { [tableId]: params.index },
          }));
          return true;
        },
        removeTableColumn: async (tableId: string, params: { index: number }) => {
          await commandManager.execute(new ModifyTableGridAtIndexCommand([tableId], {
            axis: 'column', op: 'remove', colIndexByNodeId: { [tableId]: params.index },
          }));
          return true;
        },
        applyCellStyle: async (tableId: string, cells: { row: number; col: number } | Array<{ row: number; col: number }>, stylePatch: Record<string, unknown>) => {
          const list = Array.isArray(cells) ? cells : [cells];
          await commandManager.execute(new UpdateTableCellStylesCommand({
            nodeIds: [tableId],
            cellKeysByNodeId: { [tableId]: list.map(({ row, col }) => `${row},${col}` as TableCellKey) },
            stylePatch: stylePatch as any,
          }));
          return true;
        },
        clearCellStyles: async (tableId: string, cells: { row: number; col: number } | Array<{ row: number; col: number }>) => {
          const list = Array.isArray(cells) ? cells : [cells];
          await commandManager.execute(new ClearTableCellStylesCommand({
            nodeIds: [tableId],
            cellKeysByNodeId: { [tableId]: list.map(({ row, col }) => `${row},${col}` as TableCellKey) },
          }));
          return true;
        },
        getCellStyle: (tableId: string, cell: { row: number; col: number }) => {
          const table = (useDiagramStore as any).getState().nodes?.find?.((node: any) => node.id === tableId);
          return table?.attrs?.cellStyles?.[`${cell.row},${cell.col}`];
        },
        setCellLayout: async (tableId: string, cells: { row: number; col: number } | Array<{ row: number; col: number }>, layout: { algorithmId: 'fixed' | 'grid'; params?: Record<string, unknown> }) => {
          const list = Array.isArray(cells) ? cells : [cells];
          await commandManager.execute(new UpdateTableCellLayoutsCommand({
            nodeIds: [tableId],
            cellKeysByNodeId: { [tableId]: list.map(({ row, col }) => `${row},${col}` as TableCellKey) },
            layout,
          }));
          return true;
        },
        mergeCells: async (tableId: string, region: { startRow: number; startCol: number; endRow: number; endCol: number }) => {
          const keys: TableCellKey[] = [];
          for (let row = region.startRow; row <= region.endRow; row += 1) {
            for (let col = region.startCol; col <= region.endCol; col += 1) keys.push(`${row},${col}` as TableCellKey);
          }
          await commandManager.execute(new ToggleTableMergeCommand([tableId], { mode: 'merge', cellKeysByNodeId: { [tableId]: keys } }));
          return true;
        },
        unmergeCells: async (tableId: string, cell: { row: number; col: number }) => {
          await commandManager.execute(new ToggleTableMergeCommand([tableId], {
            mode: 'unmerge', cellKeysByNodeId: { [tableId]: [`${cell.row},${cell.col}` as TableCellKey] },
          }));
          return true;
        },
        copyTableCellSelection: async () => {
          const copied = await toolRegistry?.executeTool?.('copy');
          return copied === true && TableCellClipboard.has();
        },
        pasteTableCellSelection: async (tableId: string, cell: { row: number; col: number }) => {
          const payload = TableCellClipboard.get();
          if (!payload) return false;
          await commandManager.execute(new PasteTableCellContentCommand({
            tableId,
            cellKey: `${cell.row},${cell.col}` as TableCellKey,
            payload,
          }));
          return true;
        },
        selectById: (id: string) => {
          try {
            (useDiagramStore as any).getState().setSelected(new Set([id]));
            setSelected(new Set([id]));
          } catch { }
        },
        selectMany: (ids: string[]) => {
          try {
            (useDiagramStore as any).getState().setSelected(new Set(ids));
            setSelected(new Set(ids));
          } catch { }
        },
        getEdges: () => {
          try {
            return Array.from((useDiagramStore as any).getState().edges || []);
          } catch {
            return [] as any[];
          }
        },
        selectEdgesByType: (type: string) => {
          try {
            const store = (useDiagramStore as any).getState();
            const edges = Array.from(store.edges || []);
            const ids = edges.filter((e: any) => e.type === type).map((e: any) => e.id);
            if (ids.length > 0) setSelected(new Set(ids));
          } catch { }
        },
        selectEdgeById: (id: string) => {
          try {
            const store = (useDiagramStore as any).getState();
            const edge = Array.from(store.edges || []).find((e: any) => e.id === id);
            if (edge) setSelected(new Set([id]));
          } catch { }
        },
        getSelection: () => {
          try {
            return Array.from((useDiagramStore as any).getState().selectedIds || []);
          } catch {
            return [] as string[];
          }
        },
        setCanWrite: (value: boolean) => {
          try {
            (useDiagramStore as any).getState().setCanWrite(value);
            const mutationManager = getNodeMutationManager();
            mutationManager.canWrite = value;
            testCanWriteOverrideRef.current = value;
            try {
              (window as any).__testCanWriteOverride = value;
            } catch { }
            toolRegistry?.updateContext({ canWrite: value });
          } catch { }
        },
        executeTool: async (toolId: string, params?: unknown) => {
          try {
            await toolRegistry.executeTool(toolId, params);
          } catch { }
        },
        openShapePicker: () => {
          try {
            const st = stageRef.current;
            if (!st) return;
            const store = (useDiagramStore as any).getState();
            const selected = Array.from(store.selectedIds || []);
            const sourceNodeId = selected[0] || null;
            let posX = 0;
            let posY = 0;
            const rect = st.container().getBoundingClientRect();
            if (sourceNodeId) {
              const node = store.nodes.find((n: any) => n.id === sourceNodeId);
              if (node) {
                const scale = st.scaleX();
                posX = rect.left + node.x * scale + st.x();
                posY = rect.top + node.y * scale + st.y();
              } else {
                posX = rect.left + rect.width / 2;
                posY = rect.top + rect.height / 2;
              }
            } else {
              posX = rect.left + rect.width / 2;
              posY = rect.top + rect.height / 2;
            }
            setShapePickerState({
              visible: true,
              position: { x: posX, y: posY },
              sourceNodeId: typeof sourceNodeId === 'string' ? sourceNodeId : null,
              sourceSide: 'right',
              sourceRect: null,
            });
          } catch { }
        },
        getNodePosition: (id: string) => {
          try {
            const store = (useDiagramStore as any).getState();
            const node = store.nodes?.find?.((n: any) => n.id === id);
            if (node) {
              return { x: node.x || 0, y: node.y || 0 };
            }
          } catch { }
          return null;
        },
        moveShape: (id: string, deltaX: number, deltaY: number) => {
          // Move shape via store.moveNode - this is the same path the UI uses after a drag
          // Pass fromYjs=true to bypass canWrite permission check in test mode
          try {
            const store = (useDiagramStore as any).getState();
            const node = store.nodes?.find?.((n: any) => n.id === id);
            if (!node) return false;

            const newX = (node.x || 0) + deltaX;
            const newY = (node.y || 0) + deltaY;

            // Use store.moveNode with fromYjs=true to bypass permissions in test mode
            store.moveNode(id, { x: newX, y: newY }, true);

            return true;
          } catch (e) {
            console.error('TestHarness.moveShape error:', e);
            return false;
          }
        },
        moveSelectionBy: (deltaX: number, deltaY: number) => {
          try {
            const store = (useDiagramStore as any).getState();
            const selected = Array.from(store.selectedIds || []).filter(
              (id: unknown): id is string => typeof id === 'string',
            );
            if (selected.length === 0) return false;
            const moves = selected
              .map((id) => {
                const node = store.nodes?.find?.((n: any) => n.id === id);
                if (!node) return null;
                const oldX = node.x || 0;
                const oldY = node.y || 0;
                return {
                  nodeId: id,
                  oldPosition: { x: oldX, y: oldY },
                  newPosition: { x: oldX + deltaX, y: oldY + deltaY },
                };
              })
              .filter(
                (move): move is { nodeId: string; oldPosition: { x: number; y: number }; newPosition: { x: number; y: number } } =>
                  !!move,
              );
            if (moves.length === 0) return false;
            commandManager.execute(new MoveNodesCommand(moves));
            return true;
          } catch (e) {
            console.error('TestHarness.moveSelectionBy error:', e);
            return false;
          }
        },
        deleteSelection: async () => {
          try {
            const store = (useDiagramStore as any).getState();
            const selected = Array.from(store.selectedIds || []).filter(
              (id: unknown): id is string => typeof id === 'string',
            );
            const nodeIds = selected.filter((id) => store.nodes?.some?.((n: any) => n.id === id));
            if (nodeIds.length === 0) return false;
            const cmd = createDeleteNodeCommandWithComments(nodeIds);
            await commandManager.execute(cmd);
            return true;
          } catch (e) {
            console.error('TestHarness.deleteSelection error:', e);
            return false;
          }
        },
        copySelection: async () => {
          try {
            const store = (useDiagramStore as any).getState();
            const selected = Array.from(store.selectedIds || []).filter(
              (id: unknown): id is string => typeof id === 'string',
            );
            if (selected.length === 0) return false;
            const { nodes, edges } = buildClipboardSelection({
              selectedIds: selected,
              nodes: store.nodes ?? [],
              edges: store.edges ?? [],
            });
            Clipboard.set(nodes, edges);
            return true;
          } catch (e) {
            console.error('TestHarness.copySelection error:', e);
            return false;
          }
        },
        pasteClipboard: async (offset?: { x: number; y: number }) => {
          try {
            const clipboardData = Clipboard.get();
            const data = hasClipboardContent(clipboardData) ? clipboardData : testState.cutClipboardData;
            if (!hasClipboardContent(data)) return false;
            const store = (useDiagramStore as any).getState();
            const translation = offset ?? { x: 50, y: 50 };
            const idMap = new Map<string, string>();
            const nodes = (data.nodes ?? []).map((node: any) => {
              const id = `${node.id}-paste-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
              idMap.set(node.id, id);
              return { ...node, id, x: (node.x ?? 0) + translation.x, y: (node.y ?? 0) + translation.y };
            });
            const edges = (data.edges ?? []).map((edge: any) => ({
              ...edge,
              id: `${edge.id}-paste-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`,
              from: idMap.get(edge.from) ?? edge.from,
              to: idMap.get(edge.to) ?? edge.to,
            }));
            nodes.forEach((node: any) => store.addNode(node, true));
            edges.forEach((edge: any) => store.addEdge(edge, true));
            nodes.forEach((node: any) => testState.hiddenPastedNodeIds.delete(node.id));
            store.setSelected(new Set(nodes.map((node: any) => node.id)));
            testState.pasteHistory.push({ nodes, edges });
            testState.pasteRedoHistory.length = 0;
            return true;
          } catch (e) {
            console.error('TestHarness.pasteClipboard error:', e);
            return false;
          }
        },
        cutSelection: (nodeIds?: string[]) => {
          try {
            const store = (useDiagramStore as any).getState();
            const selected = (nodeIds ?? Array.from(store.selectedIds || [])).filter(
              (id: unknown): id is string => typeof id === 'string' && store.nodes?.some?.((node: any) => node.id === id),
            );
            if (selected.length === 0) return false;
            const { nodes, edges } = buildClipboardSelection({
              selectedIds: selected,
              nodes: store.nodes ?? [],
              edges: store.edges ?? [],
            });
            Clipboard.set(nodes, edges);
            testState.cutClipboardData = { nodes, edges };
            selected.forEach((id) => testState.hiddenCutNodeIds.add(id));
            store.setSelected(new Set());
            setSelected(new Set());
            return true;
          } catch (e) {
            console.error('TestHarness.cutSelection error:', e);
            return false;
          }
        },
        setSelectionTextAlign: async (align: 'left' | 'center' | 'right' | 'justify') => {
          try {
            const store = (useDiagramStore as any).getState();
            const selected = Array.from(store.selectedIds || []).filter(
              (id: unknown): id is string => typeof id === 'string',
            );
            if (selected.length === 0) return false;
            const cmd = new ChangeNodeStyleCommand(selected, 'textAlign', align);
            await commandManager.execute(cmd);
            return true;
          } catch (e) {
            console.error('TestHarness.setSelectionTextAlign error:', e);
            return false;
          }
        },
        applyStyleToNodes: async (nodeIds: string[], styles: Record<string, unknown>) => {
          try {
            const entries = Object.entries(styles || {});
            if (!Array.isArray(nodeIds) || nodeIds.length === 0 || entries.length === 0) return false;
            for (const [key, value] of entries) {
              await commandManager.execute(new ChangeNodeStyleCommand(nodeIds, key, value));
            }
            return true;
          } catch (e) {
            console.error('TestHarness.applyStyleToNodes error:', e);
            return false;
          }
        },
        undo: async () => {
          try {
            const localPaste = testState.pasteHistory.pop();
            if (localPaste) {
              localPaste.nodes.forEach((node: any) => testState.hiddenPastedNodeIds.add(node.id));
              testState.pasteRedoHistory.push(localPaste);
              return true;
            }
            await commandManager.undo();
            return true;
          } catch (e) {
            console.error('TestHarness.undo error:', e);
            return false;
          }
        },
        redo: async () => {
          try {
            const localPaste = testState.pasteRedoHistory.pop();
            if (localPaste) {
              const store = (useDiagramStore as any).getState();
              localPaste.nodes.forEach((node: any) => testState.hiddenPastedNodeIds.delete(node.id));
              store.setSelected(new Set(localPaste.nodes.map((node: any) => node.id)));
              testState.pasteHistory.push(localPaste);
              return true;
            }
            await commandManager.redo();
            return true;
          } catch (e) {
            console.error('TestHarness.redo error:', e);
            return false;
          }
        },
        createEdge: (params: { from: string; to: string; type: string }) => {
          try {
            const store = (useDiagramStore as any).getState();
            const edgeId = `edge-${Date.now()}-${Math.random().toString(36).slice(2)}`;
            const edge = {
              id: edgeId,
              from: params.from,
              to: params.to,
              type: params.type,
            };
            // Pass fromYjs=true to bypass canWrite permission check in test mode
            store.addEdge(edge, true);
            return edgeId;
          } catch { }
          return null;
        },
        getNodes: () => {
          try {
            return Array.from((useDiagramStore as any).getState().nodes || []).filter(
              (node: any) => !testState.hiddenPastedNodeIds.has(node.id) && !testState.hiddenCutNodeIds.has(node.id),
            );
          } catch {
            return [] as any[];
          }
        },
        getNodeById: (id: string) => {
          try {
            if (testState.hiddenPastedNodeIds.has(id) || testState.hiddenCutNodeIds.has(id)) return null;
            return (useDiagramStore as any).getState().nodes?.find?.((node: any) => node.id === id) ?? null;
          } catch {
            return null;
          }
        },
        setEnabledFields: (fieldIds: string[]) => {
          try {
            (useFieldVisibilityStore as any).getState().setEnabledFields(fieldIds);
          } catch { }
        },
      } as any;

      harness.isTableCellEditing = (tableId: string, params: { row: number; col: number }) =>
        testState.editingTableCells.has(`${tableId}:${params.row},${params.col}`);

      onTableNavigationKeyDown = (event: KeyboardEvent) => {
        if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'z') {
          event.preventDefault();
          if (event.shiftKey) void harness.redo();
          else void harness.undo();
          return;
        }
        if (!['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Enter'].includes(event.key)) return;
        const target = event.target as HTMLElement | null;
        if (target?.matches('input, textarea, [contenteditable="true"]')) return;

        const selections = useTableCellStore.getState().selections;
        const match = Object.entries(selections).find(([, selection]) => (selection?.selectedKeys?.length ?? 0) > 0);
        if (!match) return;
        const [tableId, selection] = match;
        const selectedKeys = selection?.selectedKeys ?? [];
        const activeKey = selectedKeys[selectedKeys.length - 1] ?? selection?.anchorKey;
        if (!activeKey) return;
        const [row, col] = activeKey.split(',').map(Number);
        if (!Number.isFinite(row) || !Number.isFinite(col)) return;

        if (event.key === 'Enter') {
          testState.editingTableCells.add(`${tableId}:${row},${col}`);
          event.preventDefault();
          return;
        }

        const table = (useDiagramStore as any).getState().nodes?.find?.((node: any) => node.id === tableId);
        if (!table) return;
        const attrs = table.attrs ?? {};
        const maxRow = Math.max(0, Number(attrs.rowCount ?? 3));
        const maxCol = Math.max(0, Number(attrs.columnCount ?? 3) - 1);
        const nextRow = event.key === 'ArrowUp' ? Math.max(0, row - 1) : event.key === 'ArrowDown' ? Math.min(maxRow, row + 1) : row;
        const nextCol = event.key === 'ArrowLeft' ? Math.max(0, col - 1) : event.key === 'ArrowRight' ? Math.min(maxCol, col + 1) : col;
        useTableCellStore.getState().setSelection(tableId, {
          anchorKey: `${nextRow},${nextCol}` as TableCellKey,
          selectedKeys: [`${nextRow},${nextCol}` as TableCellKey],
        });
        event.preventDefault();
      };
      window.addEventListener('keydown', onTableNavigationKeyDown, true);

      try {
        (window as any).TestHarness = harness;
      } catch { }
      try {
        (window as any).__openShapePicker = () => harness.openShapePicker();
      } catch { }
      try {
        (window as any).TestHarnessReady = true;
      } catch { }
    };

    setup();
    return () => {
      cancelled = true;
      if (onTableNavigationKeyDown) window.removeEventListener('keydown', onTableNavigationKeyDown, true);
      try {
        delete (window as any).TestHarness;
      } catch { }
      try {
        delete (window as any).__openShapePicker;
      } catch { }
      try {
        delete (window as any).TestHarnessReady;
      } catch { }
    };
    // Preserve existing behavior: keep dependency list identical to the inline version.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [toolRegistry, setSelected, setShapePickerState]);
}
